import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getClientMeasurementHistory } from '../../../services/metrics.service';

interface ClientMeasurementChartsProps {
    clientId: string;
}

interface ChartDataPoint {
  timestamp: number;
  value: number;
}

interface GroupedData {
  [key: string]: {
    name: string;
    unit: string;
    data: ChartDataPoint[];
  };
}

const formatDateTick = (timestamp: number) => {
  return new Date(timestamp).toLocaleDateString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
};

// Function to calculate Y-axis domain with padding
const getPaddedDomain = (dataMin: number, dataMax: number): [number, number] => {
  if (dataMin === dataMax) {
    return [dataMin - 1, dataMax + 1];
  }
  const padding = (dataMax - dataMin) * 0.1; // 10% padding
  return [Math.floor(dataMin - padding), Math.ceil(dataMax + padding)];
};

export const ClientMeasurementCharts: React.FC<ClientMeasurementChartsProps> = ({ clientId }) => {
  const [groupedData, setGroupedData] = useState<GroupedData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAndGroupHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const history = await getClientMeasurementHistory(clientId, { limit: 500 });

      if (!Array.isArray(history)) {
        throw new Error('Invalid data format from API');
      }

      const grouped = history.reduce<GroupedData>((acc, item) => {
        const typeName = item.measurementType.name;
        if (!acc[typeName]) {
          acc[typeName] = {
            name: typeName,
            unit: item.unit.symbol,
            data: [],
          };
        }
        acc[typeName].data.push({
          timestamp: new Date(item.measuredAt).getTime(),
          value: item.value,
        });
        return acc;
      }, {});

      // Sort data points by timestamp for each group
      Object.values(grouped).forEach(group => {
        group.data.sort((a, b) => a.timestamp - b.timestamp);
      });

      setGroupedData(grouped);
    } catch (err) {
      setError('Не вдалося завантажити дані для графіків клієнта.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchAndGroupHistory();
  }, [fetchAndGroupHistory]);

  if (loading) {
    return <Box display="flex" justifyContent="center" sx={{ my: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (Object.keys(groupedData).length === 0) {
    return <Typography>Недостатньо даних для побудови графіків.</Typography>;
  }

  return (
    <Grid container spacing={4}>
      {Object.values(groupedData).map(group => {
        const values = group.data.map(d => d.value);
        const yDomain = getPaddedDomain(Math.min(...values), Math.max(...values));

        return (
          <Grid item xs={12} md={6} key={group.name}>
            <Paper sx={{ p: 2, height: 300 }}>
              <Typography variant="h6" align="center">{group.name}</Typography>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={group.data} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    tickFormatter={formatDateTick}
                    angle={-20}
                    textAnchor="end"
                    height={50}
                  />
                  <YAxis unit={group.unit} domain={yDomain} allowDataOverflow={true} />
                  <Tooltip
                    labelFormatter={(label) => new Date(label).toLocaleDateString('uk-UA', { day: '2-digit', month: 'long', year: 'numeric' })}
                    formatter={(value, name, props) => [`${value} ${props.unit}`, name]}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name={group.name}
                    stroke="#8884d8"
                    unit={group.unit}
                    dot={true}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
};
